import React from 'react';
import styled from 'styled-components';

import { GovernedAnalysisChart, GovernedAnalysisSegment, SupportAction } from '../../../types';
import { ResponseActionButton } from '../../ResponseActionButton';
import {
  RESPONSE_SERIES,
  responseTone,
  responseType,
  rsColor,
  rsSize,
  rsv
} from '../../responseScale';

/** A segment's colour: its tone, else the series palette by position. */
export const segmentInk = (segment: GovernedAnalysisSegment, index: number): string =>
  segment.tone ? responseTone(segment.tone).ink : RESPONSE_SERIES[index % RESPONSE_SERIES.length];

// 6424:11857.
const Card = styled.div`
  display: flex;
  align-items: center;
  padding: ${rsv.space('chartCard')};
  border: 1px solid ${rsColor.border};
  border-radius: ${rsv.radius('row')}px;
  background: ${rsColor.inset};
`;

const Donut = styled.div`
  position: relative;
  flex: 0 0 auto;
  width: ${rsv.size('donut')}px;
  height: ${rsv.size('donut')}px;

  > svg {
    display: block;
    width: 100%;
    height: 100%;
  }
`;

const Centre = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const TotalLabel = styled.span`
  color: ${rsColor.muted};
  ${responseType('totalLabel')}
`;

const TotalValue = styled.b`
  margin-top: ${rsv.space('donutTotalGap')}px;
  color: ${rsColor.text};
  ${responseType('total')}
`;

const Side = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  margin-left: ${rsv.space('chartGap')}px;
`;

const Legend = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

// 6424:11868: 40 tall, ruled except the last.
const LegendRow = styled.li`
  display: flex;
  align-items: center;
  box-sizing: border-box;
  min-height: ${rsv.size('legendRow')}px;
  color: ${rsColor.text};
  ${responseType('legend')}

  & + & {
    margin-top: ${rsv.space('legendRowGap')}px;
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${rsColor.border};
  }
`;

const Dot = styled.i<{ $ink: string }>`
  flex: 0 0 auto;
  width: ${rsv.size('legendDot')}px;
  height: ${rsv.size('legendDot')}px;
  border-radius: 50%;
  background: ${({ $ink }) => $ink};
`;

const LegendLabel = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  margin-left: ${rsv.space('legendDotGap')}px;
`;

const LegendValue = styled.span`
  flex: 0 0 auto;
  margin-left: ${rsv.space('legendDotGap')}px;
  font-weight: 700;
`;

const Foot = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${rsv.space('legendToFoot')}px;

  > * + * {
    margin-left: ${rsv.space('chartActionGap')}px;
  }
`;

const CIRCUMFERENCE = 2 * Math.PI * rsSize.donutRadius;
const CENTRE = rsSize.donut / 2;

// The ring is drawn in the page's units; the panel shrinks the box and the ring with it.

/** Each segment's arc length and where it starts, clockwise from 12 o'clock. */
const arcs = (segments: GovernedAnalysisSegment[]) => {
  const shares = segments.map(segment => Math.max(0, segment.share));
  // Shares are 0-100; a set summing past 100 is scaled to fit the ring.
  const whole = Math.max(
    100,
    shares.reduce((sum, share) => sum + share, 0)
  );
  let start = 0;
  return shares.map(share => {
    const length = (share / whole) * CIRCUMFERENCE;
    const arc = { length, start };
    start += length;
    return arc;
  });
};

export interface IGovernedDonutProps {
  chart: GovernedAnalysisChart;
  onAction: (action: SupportAction) => void;
}

/** 6424:11857: the donut, its centre total, the legend and the icon-only chart actions. */
const GovernedDonut: React.FC<IGovernedDonutProps> = ({ chart, onAction }) => {
  const segmentArcs = arcs(chart.segments);
  return (
    <Card data-testid="orbit-response-chart">
      <Donut>
        {/* The legend carries the same values as text. */}
        <svg viewBox={`0 0 ${rsv.size('donut')} ${rsv.size('donut')}`} aria-hidden="true">
          <g transform={`rotate(-90 ${CENTRE} ${CENTRE})`}>
            {chart.segments.map((segment, index) => (
              <circle
                key={segment.key}
                data-segment={segment.key}
                cx={CENTRE}
                cy={CENTRE}
                r={rsSize.donutRadius}
                fill="none"
                stroke={segmentInk(segment, index)}
                strokeWidth={rsSize.donutRing}
                strokeDasharray={`${segmentArcs[index].length} ${CIRCUMFERENCE}`}
                strokeDashoffset={-segmentArcs[index].start}
              />
            ))}
          </g>
        </svg>
        {chart.total ? (
          <Centre>
            <TotalLabel>{chart.total.label}</TotalLabel>
            <TotalValue>{chart.total.value}</TotalValue>
          </Centre>
        ) : null}
      </Donut>
      <Side>
        <Legend>
          {chart.segments.map((segment, index) => (
            <LegendRow key={segment.key} data-testid="orbit-response-legend-row">
              <Dot $ink={segmentInk(segment, index)} aria-hidden="true" />
              <LegendLabel>{segment.label}</LegendLabel>
              <LegendValue>{segment.value}</LegendValue>
            </LegendRow>
          ))}
        </Legend>
        {chart.actions?.length ? (
          <Foot>
            {chart.actions.map(action => (
              <ResponseActionButton key={action.id} action={action} onAction={onAction} iconOnly />
            ))}
          </Foot>
        ) : null}
      </Side>
    </Card>
  );
};

export default GovernedDonut;
